<template>
  <div class="table-container">
  <a-table class="table dataTable no-footer" :columns="computedColumns" :data-source="data"
    :row-selection="rowSelection" :pagination="false">
    <template #bodyCell="{ column, record }">
      <template v-if="column.key === 'product'">
        <p class="text-capitalize">{{ record.product }}</p>
      </template>
      <template v-if="column.key === 'annual_cost'">
        <h6 class="table-avatar d-flex align-items-center">
          {{ record.annual_cost }}
        </h6>
      </template>
      <template v-if="column.key === 'action'">
        <div class="icon-field">
          <button type="button" class="btn table_action_btn" @click="viewMore(record.key)"><i class="ti ti-eye"></i>
            Edit</button>
        </div>
      </template>
    </template>
  </a-table>
</div>
  <!-- Fixed Pagination -->
  <div class="pagination-container" v-if="data.length > 0">
    <a-pagination :current="pagination.current" :pageSize="pagination.pageSize" :total="pagination.total"
      showSizeChanger :pageSizeOptions="pageSizeOptions" @change="handleTableChange" class="d-flex" />
  </div>
</template>

<script>
import { ref, computed, watchEffect } from "vue";
import { useRoute, useRouter } from "vue-router";
import apiClient from "@/axios";

export default {
  props: {
    searchOppQuery: String,
    filters: Object,
    fromQuote: Boolean,
  },
  data() {
    return {
      rowSelection: {
        onChange: () => { },
        onSelect: () => { },
        onSelectAll: () => { },
      },
    };
  },
  setup(props, { emit }) {
    const router = useRouter();
    const route = useRoute();
    const data = ref([]);
    const pagination = ref({
      current: 1,
      pageSize: 10,
      total: 0,
    });

    const pageSizeOptions = computed(() => {
      let options = [10]; // Default option
      if (pagination.value.total > 10) options.push(20);
      if (pagination.value.total > 20) options.push(50);
      if (pagination.value.total > 50) options.push(100);
      return options;
    });

    const fetchOpportunities = async () => {
      const id = route.query.opp_Id;
      try {
        const response = await apiClient.get(`/opportunity/view-quote-details/${id}`);
        if (response.data.status) {
          data.value = response.data.data.map((quote) => ({
            key: quote.id,
            uplift: quote.uplift,
            term: quote.term,
            supplier_name: quote.supplier_name,
            standing_charge: quote.standing_charge,
            day_unit_rate: quote.day_unit_rate,
            annual_cost: quote.price ? "£" + quote.price : '',
            night_unit_rate: quote.night_unit_rate,
            evening_and_weekend: quote.evening_and_weekend,
            product: quote.energy,
            created_at: formatDate(quote.created_at),
            opportunity_id: quote.opportunity_id,
            supplier_id: quote.supplier_id,
            energy: quote.energy,
            price_book_version: quote.price_book_version,
            full_description: quote.full_description,
            price: quote.price || "",
          }));
          pagination.value.total = response.data.data.total;
        } else {
          console.error("Failed to fetch opportunities:", response);
        }
      } catch (error) {
        console.error("Error fetching opportunities:", error);
      }
    };

    const handleTableChange = (current, pageSize) => {
      pagination.value.current = current;
      pagination.value.pageSize = pageSize;
      // fetchOpportunities();
    };

    const openEditModal = (id) => {
      router.push({
        path: "/crm/create-organisation",
        query: {
          org_Id: id,
        },
      });
    };

    // Define the base columns array
    const baseColumns = [
      {
        title: "Product",
        dataIndex: "product",
        key: "product",
        sorter: {
          compare: (a, b) => a.product - b.product,
        },
      },
      {
        title: "Unit Rate Uplift",
        dataIndex: "uplift",
        key: "uplift",
        sorter: {
          compare: (a, b) => a.uplift - b.uplift,
        },
      },
      {
        title: "Standing Charge Uplift (pence/day)",
        dataIndex: "standing_charge",
        key: "standing_charge",
        sorter: {
          compare: (a, b) => a.standing_charge - b.standing_charge,
        },
      },
      {
        title: "Supplier Name",
        dataIndex: "supplier_name",
        key: "supplier_name",
        sorter: {
          compare: (a, b) => a.supplier_name.localeCompare(b.supplier_name),
        },
      },
      {
        title: "Created At",
        dataIndex: "created_at",
        key: "created_at",
        sorter: {
          compare: (a, b) => a.created_at.localeCompare(b.created_at),
        },
      },
      {
        title: "Term",
        dataIndex: "term",
        key: "term",
        sorter: {
          compare: (a, b) => a.term - b.term,
        },
      },
      {
        title: "Day Rate (p/kWh)",
        dataIndex: "day_unit_rate",
        key: "day_unit_rate",
        sorter: {
          compare: (a, b) => a.day_unit_rate - b.day_unit_rate,
        },
      },
      {
        title: "Annual cost",
        dataIndex: "annual_cost",
        key: "annual_cost",
        sorter: {
          compare: (a, b) => a.annual_cost - b.annual_cost,
        },
      }
    ];

    const computedColumns = computed(() => {
      // Check if there is any non-null night_unit_rate in data
      const hasNightRate = data.value.some((record) => record.night_unit_rate !== null);
      const hasWeekendRate = data.value.some((record) => record.evening_and_weekend !== null);

      // Start with the base columns
      const dynamicColumns = [...baseColumns];

      // Conditionally add "Night Rate" column if any record has a night_unit_rate
      if (hasNightRate) {
        dynamicColumns.push({
          title: "Night Rate (p/kWh)",
          dataIndex: "night_unit_rate",
          key: "night_unit_rate",
          sorter: {
            compare: (a, b) => a.night_unit_rate - b.night_unit_rate,
          },
        });
      }

      // Conditionally add "Weekend Rate" column if any record has a evening_and_weekend
      if (hasWeekendRate) {
        dynamicColumns.push({
          title: "Weekend Rate (p/kWh)",
          dataIndex: "evening_and_weekend",
          key: "evening_and_weekend",
          sorter: {
            compare: (a, b) => a.evening_and_weekend - b.evening_and_weekend,
          },
        });
      }

      if (props.fromQuote) {
        dynamicColumns.push({
          title: "Action",
          dataIndex: "Action",
          key: "action",
        })
      }

      return dynamicColumns;
    });
    // Function to convert date format
    const formatDate = (dateTime) => {
      // Create a Date object
      const date = new Date(dateTime);

      // Extract day, month, year, hours, and minutes
      const day = String(date.getUTCDate()).padStart(2, "0"); // Ensure 2-digit day
      const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // Months are 0-indexed
      const year = date.getUTCFullYear();
      const hours = String(date.getUTCHours()).padStart(2, "0"); // UTC hours
      const minutes = String(date.getUTCMinutes()).padStart(2, "0"); // UTC minutes

      // Return formatted date-time
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    };

    const viewMore = (id) => {
      const quote = data.value.find(item => item.key === id)
      emit('editQuote', quote)
    }

    watchEffect(() => {
      fetchOpportunities();
    });

    return {
      data,
      pagination,
      formatDate,
      fetchOpportunities,
      handleTableChange,
      openEditModal,
      computedColumns,
      pageSizeOptions,
      viewMore
    };
  },
};
</script>
